import { useEffect, useState } from "react";
import CustomPieChart from "../components/CustomPieChart";
import StatCard from "../components/StatCard";
import CustomBarChart from "../components/CustomBarChart";
import SplashScreen from "../components/SplashScreen";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import MapDash from "../components/MapDash";
import { useTranslation } from "react-i18next";

const Water = () => {
  const [data, setData] = useState([]);
  const [selectedCity, setselectedCity] = useState("all");
  const { t } = useTranslation();

  const navigate = useNavigate();

  useEffect(() => {
    const savedData =
      JSON.parse(localStorage.getItem("waterFormDataStack")) || [];
    setData(savedData);
  }, []);

  const filteredData =
    selectedCity === "all"
      ? data
      : data.filter((item) => item.city?.trim() === selectedCity.trim());

  const cityNames = [
    "all",
    ...new Set(data.map((item) => item.city?.trim()).filter(Boolean)),
  ];

  const getFilteredAvg = (key) => {
    const values = filteredData.map((item) => Number(item[key]) || 0);
    if (values.length === 0) return 0;
    const sum = values.reduce((acc, val) => acc + val, 0);
    return Math.round(sum / values.length);
  };

  const getFilteredSewageVolume = () => {
    const fields = [
      "sewageVolume_2021",
      "sewageVolume_2022",
      "sewageVolume_2023",
      "sewageVolume_2024",
    ];
    const values = filteredData
      .map((item) =>
        fields
          .map((field) => Number(item[field]) || 0)
          .filter((val) => val !== 0)
      )
      .flat();

    if (values.length === 0) return 0;
    const sum = values.reduce((acc, val) => acc + val, 0);
    return Math.round(sum / values.length);
  };

  const getResponseStatusCount = () => {
    const statusCount = { مقبول: 0, مرفوض: 0 };
  
    filteredData.forEach((item) => {
      const status = item.responseStatus?.trim();
      if (status === "مقبول") statusCount.مقبول += 1;
      else if (status === "مرفوض") statusCount.مرفوض += 1;
    });
  
    return statusCount;
  };
  const responseStats = getResponseStatusCount();

  return (
    <>
      <Helmet>
        <title>{t("water")} | {t("dashboard")}</title>
      </Helmet>

      <div className="flex flex-col gap-4 md:gap-3 text-start w-full h-auto md:h-full md:min-h-0 md:overflow-hidden">
        <div className="flex flex-col gap-2 text-start">
          <h1 className="mx-auto text-xl sm:text-2xl md:text-3xl font-extrabold p-2.5 bg-white/55 rounded-md backdrop-blur-md w-full flex justify-center">
            {t("water")}
          </h1>
          <div className="flex flex-col sm:flex-row gap-2 w-full justify-between items-center">
            <select
              name="station"
              className="border p-2 rounded w-full sm:w-1/2 md:w-1/3 lg:w-1/4 dark:bg-gray-600 dark:text-white bg-white text-xs sm:text-sm"
              value={selectedCity}
              onChange={(e) => setselectedCity(e.target.value)}
            >
              {cityNames.map((city, index) => (
                <option key={index} value={city}>
                  {city === "all" ? t("average_all_cities") : city}
                </option>
              ))}
            </select>

            <button
              onClick={() => navigate("/WaterForm")}
              className="bg-green-500 hover:bg-green-600 transition-colors text-white p-2 rounded w-full sm:w-auto text-xs sm:text-sm font-semibold"
            >
              {t("edit_data")}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1 min-h-0">
          {/* الجزء الأول: الكروت */}
          <div className="col-span-1 overflow-y-auto pr-2 h-[60vh] md:h-auto md:flex-1 md:min-h-0 flex flex-col gap-4" dir="ltr">
            <div className="bg-white/30 dark:bg-gray-800/50 p-3 rounded-lg shadow-sm" dir={document.documentElement.dir}>
              <h2 className="text-base sm:text-lg font-bold mb-3">{t("water_consumption")}</h2>
              <div className="grid grid-cols-2 gap-3">
                <StatCard
                  title={t("water_consumption")}
                  value={`${getFilteredAvg("waterConsumption")} م³/سنة`}
                />
                <StatCard
                  title={t("leakage_percentage")}
                  value={`${getFilteredAvg("leakagePercent")} %`}
                />
              </div>
            </div>

            <div className="bg-white/30 dark:bg-gray-800/50 p-3 rounded-lg shadow-sm" dir={document.documentElement.dir}>
              <h2 className="text-base sm:text-lg font-bold mb-3">{t("desalination_plants")}</h2>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <StatCard
                    title={t("beneficiary_population")}
                    value={`${getFilteredAvg("beneficiaryPopulation")} %`}
                  />
                </div>
                <div>
                  <StatCard
                    title={t("input_water_volume")}
                    value={`${getFilteredAvg("inputWaterVolume")} %`}
                  />
                </div>
              </div>
            </div>

            <div className="bg-white/30 dark:bg-gray-800/50 p-3 rounded-lg shadow-sm" dir={document.documentElement.dir}>
              <h2 className="text-base sm:text-lg font-bold mb-3">
                {t("treatment_vs_desalination")}
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <StatCard
                  title={t("lost_water_volume")}
                  value={`${getFilteredAvg("lostWaterVolume")} %`}
                />
                <StatCard
                  title={t("actual_treated_volume")}
                  value={`${getFilteredAvg("actualTreatedVolume")} %`}
                />
                <StatCard
                  title={t("accepted_cases")}
                  value={`${responseStats.مقبول}`}
                />
                <StatCard
                  title={t("rejected_cases")}
                  value={`${responseStats.مرفوض}`}
                />
              </div>
            </div>

            <div className="bg-white/30 dark:bg-gray-800/50 p-3 rounded-lg shadow-sm" dir={document.documentElement.dir}>
              <h2 className="text-base sm:text-lg font-bold mb-3">{t("sewage_water_volume")}</h2>
              <div className="grid grid-cols-1 gap-3">
                <StatCard
                  title={t("sewage_water_volume")}
                  value={`${getFilteredSewageVolume()} م³/سنة`}
                />
              </div>
            </div>
          </div>

          {/* العمود الخاص بالخريطة - 2/3 */}
          <div className="md:col-span-2 h-[60vh] md:h-auto md:flex-1 md:min-h-0 rounded-xl leaflet-container !bg-transparent min-h-[300px] shadow-md border border-white/20">
            <MapDash initialData={data} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Water;


